import React, { ReactNode, memo, forwardRef, Ref, useId } from 'react';
import classnames from 'classnames';
import { useTheme, createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useFormikError, useOptionalField } from './FormikFields';
import { ErrorMsg } from './ErrorMsg';
import { InputLabel } from './InputLabel';

interface StyleProps {
  small?: boolean;
  noMargin?: boolean;
  noBottomMargin?: boolean;
  fullHeight?: boolean;
}

interface Props extends StyleProps {
  id?: string;
  className?: string;
  label?: ReactNode;
  secondaryLabel?: ReactNode;
  type?: string;
  containerClass?: string;
  value?: any;
  name?: string;
  error?: string;
  warn?: string;
  noError?: boolean;
  preview?: boolean;
  component: React.ComponentType<any> | string;
  disabled?: boolean;
  hoverRightIcon?: React.ReactNode;
  insideRightIcon?: string;
  disableLogs?: boolean;
  requiredStar?: boolean;
  [componentProps: string]: any;
  preFieldComponent?: React.ReactNode;
  rightAction?: React.ReactNode;
}
export type GenericInputProps = Props;

export const defaultInputStyleFunction = function (theme: Theme) {
  return {
    container: {
      marginBottom: ({ noMargin, noBottomMargin }: StyleProps) =>
        noBottomMargin || noMargin ? 0 : theme.marginBase * 3,
      marginTop: ({ noMargin }: StyleProps) =>
        noMargin ? 0 : theme.marginBase * 3,
      width: '100%',
      display: 'block',
    },
    disabledContainer: {
      opacity: 0.5,
      '& > label': {
        cursor: 'auto',
      },
    },
    rightIcon: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      right: 0,
      top: 0,
      bottom: 0,
      fontSize: theme.iconSize.regular,
      padding: theme.marginBase,
      color: theme.colors.inkTertiary,
    },
    hoverRightIcon: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      right: theme.marginBase * 2,
      top: theme.marginBase,
      opacity: 0,
      '&:hover': {
        opacity: 1,
        cursor: 'pointer',
      },
    },
    inputAndPreField: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: theme.marginBase + 'px',

      position: 'relative',
    },
    inputContainer: {
      position: 'relative',
      flexGrow: 1,
      maxWidth: '100%',
      height: ({ fullHeight }: StyleProps) => (fullHeight ? '100%' : 'auto'),
    },
    input: {
      ...theme.fonts.caption,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.colors.grayBorder,
      borderRadius: theme.borderRadius.std,
      padding: `${theme.marginBase / 2}px ${theme.marginBase * 2}px`,
      minHeight: theme.height.regular,
      width: '100%',
      transition: 'border-color 0.3s',
      boxShadow: 'none',

      '&:hover + div, &:focus + div, &:active + div': {
        opacity: 1,
        cursor: 'pointer',
      },

      '&::placeholder': {
        color: theme.colors.inkTertiary,
        opacity: 0.6,
      },
      '&:focus': {
        borderColor: theme.colors.blue,
      },
      '&:invalid:not(:focus):not(:placeholder-shown)': {
        borderColor: theme.colors.red,
      },
      [theme.media.mobile]: {
        //disable zoom for iPhone users
        fontSize: 16,
      },
    },
    inputSmall: {
      minHeight: theme.height.small,
    },
    withIcon: {
      paddingRight: theme.iconSize.small + theme.marginBase * 2,
    },
    invalid: {
      borderColor: theme.colors.red,
    },
    warn: {
      borderColor: theme.colors.yellowAccessible,
    },
    star: {
      color: theme.colors.redAccessible,
    },
    labelContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  };
};

const useStyles = createUseStyles<string, StyleProps, Theme>(
  defaultInputStyleFunction,
);

export const GenericInput = memo(
  forwardRef(
    (
      {
        label,
        secondaryLabel,
        containerClass,
        className,
        error,
        warn,
        noError,
        component,
        noMargin,
        noBottomMargin,
        insideRightIcon,
        hoverRightIcon,
        disabled,
        preview,
        placeholder,
        small,
        disableLogs,
        requiredStar,
        preFieldComponent,
        rightAction,
        fullHeight,
        ...rest
      }: Props,
      ref: Ref<any>,
    ) => {
      const theme: Theme = useTheme();
      const classes = useStyles({
        theme,
        small,
        noMargin,
        noBottomMargin,
        fullHeight,
      });
      const { field } = useOptionalField(rest);
      const errorMsg = useFormikError({ ...rest, error });

      const generatedId = useId();
      const id = rest.id || generatedId;

      const Component = component;

      return (
        <div
          className={classnames(containerClass, classes.container, {
            [classes.disabledContainer]: !!disabled && !preview,
          })}
        >
          <InputLabel
            label={label}
            rightAction={rightAction}
            secondaryLabel={secondaryLabel}
            htmlFor={id}
            requiredStar={requiredStar}
          />
          <div className={classes.inputAndPreField}>
            {preFieldComponent}
            <div className={classes.inputContainer}>
              <Component
                className={classnames(className, classes.input, {
                  [classes.invalid]: !!errorMsg,
                  [classes.warn]: !!warn,
                  [classes.withIcon]: !!insideRightIcon,
                  [classes.inputSmall]: !!small,
                })}
                ref={ref}
                {...field}
                {...rest}
                disabled={disabled}
                small={
                  typeof component === 'string' ? small?.toString() : small
                }
                id={id}
                placeholder={placeholder}
              />

              {insideRightIcon && (
                <i className={classnames(classes.rightIcon, insideRightIcon)} />
              )}
              {hoverRightIcon && (
                <div className={classes.hoverRightIcon}>{hoverRightIcon}</div>
              )}
            </div>
          </div>

          {!noError && <ErrorMsg error={errorMsg} />}
          {!!warn && <ErrorMsg error={warn} variant="warn" />}
        </div>
      );
    },
  ),
);
GenericInput.displayName = 'GenericInput';
